import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Store, Truck, ShieldCheck } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1707049846876-44e39a6342bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxzdXBlcm1hcmtldCUyMGdyb2NlcnklMjBzdG9yZXxlbnwwfHx8fDE3ODk3NzY1NzV8MA&ixlib=rb-4.1.0&q=85";

export default function AuthScreen() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const doLogin = async (em, pw) => {
    setLoading(true);
    try {
      await login(em.trim(), pw);
      toast.success("Welcome back to SARAVANA DELAPP");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e?.preventDefault();
    doLogin(email, password);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="supermarket" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/85 mix-blend-multiply" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-primary-foreground">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 backdrop-blur">
              <Store className="h-6 w-6" />
            </div>
            <span className="font-display text-2xl font-extrabold tracking-tight">SARAVANA DELAPP</span>
          </div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="font-display text-4xl font-extrabold leading-tight lg:text-5xl">
              Supermarket accounts &amp; delivery, under control.
            </h1>
            <p className="mt-5 max-w-md text-base text-primary-foreground/80">
              Track customers, multi-bill deliveries, delivery staff and payments — with area-wise reports exportable to
              PDF &amp; Excel.
            </p>
          </motion.div>
          <div className="flex gap-8 text-sm text-primary-foreground/70">
            <span className="flex items-center gap-2"><Truck className="h-4 w-4" /> Delivery mapping</span>
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Role-based access</span>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <Store className="h-6 w-6 text-primary" />
            <span className="font-display text-xl font-extrabold">SARAVANA DELAPP</span>
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">Access your store operations dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" autoComplete="off">
            <div className="space-y-2">
              <Label htmlFor="email">Email or Phone</Label>
              <Input
                id="email"
                type="text"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email or phone number"
                autoComplete="off"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
                required
              />
            </div>
            <Button type="submit" className="w-full" data-testid="login-submit-button" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
